import { type FC, useCallback } from 'react';
import { type Headers, toCSV } from 'react-csv/lib/core';
import { Icon } from '@timeedit/ui-components';
import { Button, Tooltip } from '@timeedit/ui-components/antd';
import './CsvDownloadButton.scss';

type Props = {
  rows: Record<string, unknown>[];
  headers: Headers;
  filename: string;
  enabled?: boolean;
  /** Shown on hover while disabled, to explain why there is nothing to download. */
  disabledTooltip?: string;
};

const CsvDownloadButton: FC<Props> = ({
  rows,
  headers,
  filename,
  enabled = true,
  disabledTooltip,
}) => {
  const onClick = useCallback(() => {
    if (!enabled) return;
    downloadCSV(toCSV(rows, headers), filename);
  }, [rows, headers, filename, enabled]);

  const button = (
    <Button
      icon={<Icon id="DownloadOutlined" alt="" />}
      className="csv-download-button"
      disabled={!enabled}
      onClick={onClick}
    >
      Download (.csv)
    </Button>
  );

  if (enabled || !disabledTooltip) {
    return button;
  }

  // A disabled button fires no hover events, so the tooltip needs the wrapper to
  // listen on.
  return (
    <Tooltip title={disabledTooltip}>
      <span className="csv-download-button-wrapper">{button}</span>
    </Tooltip>
  );
};

const downloadCSV = (csv: string, filename: string) => {
  const blob = new Blob([csv], { type: 'text/csv' });
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.setAttribute('download', filename);
  link.click();

  window.setTimeout(() => {
    // next tick
    window.URL.revokeObjectURL(link.href);
  }, 0);
};

/** `{date}_{objectType}_{operation}` — shared by every job result download. */
export const csvFilename = (parts: (string | undefined)[]) =>
  `${parts.filter(Boolean).join('_')}.csv`;

export default CsvDownloadButton;
